import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { studioService } from '../../utils/studioService';
import studioApplicationService from '../../utils/studioApplicationService';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const AdminStudioManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [studios, setStudios] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationStats, setApplicationStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState('applications'); // 'applications' or 'studios'
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedStudios, setSelectedStudios] = useState([]);
  const [showStudioModal, setShowStudioModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [editingStudio, setEditingStudio] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load studios and applications from database
  const loadStudios = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load studios
      if (typeof studioService.getAllStudios !== 'function') {
        throw new Error('getAllStudios is not a function - available methods: ' + Object.keys(studioService).join(', '));
      }

      const { data, error } = await studioService.getAllStudios();

      if (error) {
        setError(error.message || error);
        console.error('Error loading studios:', error);
      } else {
        setStudios(data || []);
      }

      // Load applications
      const appsResult = await studioApplicationService.getAllApplications();
      console.log('📋 Applications result:', appsResult);
      if (appsResult.success) {
        console.log('✅ Setting applications:', appsResult.data);
        setApplications(appsResult.data || []);
      } else {
        console.error('❌ Failed to load applications:', appsResult.error);
      }

      // Load application stats
      const statsResult = await studioApplicationService.getApplicationStats();
      console.log('📊 Stats result:', statsResult);
      if (statsResult.success) {
        setApplicationStats(statsResult.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudios();
  }, []);

  const filteredStudios = studios.filter(studio => {
    const matchesSearch = (studio.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (studio.owner?.full_name || studio.owner_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || studio.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleStudioAction = async (action, studioId) => {
    if (action === 'delete') {
      if (confirm('Are you sure you want to delete this studio? This action cannot be undone.')) {
        const { error } = await studioService.deleteStudio(studioId);
        if (error) {
          alert('Error deleting studio: ' + error);
        } else {
          loadStudios(); // Reload the list
        }
      }
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedStudios.length === 0) return;

    if (confirm(`Are you sure you want to ${action} ${selectedStudios.length} studio(s)?`)) {
      const { error } = await studioService.bulkUpdateStudioStatus(selectedStudios, action);
      if (error) {
        alert('Error performing bulk action: ' + error);
      } else {
        setSelectedStudios([]);
        loadStudios(); // Reload the list
      }
    }
  };

  const handleApproveApplication = async (applicationId) => {
    setActionLoading(true);
    setError(null);

    try {
      const result = await studioApplicationService.approveApplication(applicationId);

      if (result.success) {
        setSuccessMessage('Studio application approved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadStudios(); // Reload data
      } else {
        setError(result.error || 'Failed to approve application');
      }
    } catch (err) {
      setError('Failed to approve application. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectApplication = async (applicationId, reason) => {
    setActionLoading(true);
    setError(null);

    try {
      const result = await studioApplicationService.rejectApplication(applicationId, reason);

      if (result.success) {
        setSuccessMessage('Application rejected successfully.');
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowRejectModal(false);
        setSelectedApplication(null);
        setRejectionReason('');
        await loadStudios(); // Reload data
      } else {
        setError(result.error || 'Failed to reject application');
      }
    } catch (err) {
      setError('Failed to reject application. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const StudioModal = ({ studio, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      name: studio?.name || '',
      description: studio?.description || '',
      website: studio?.website || '',
      revenue_share_percentage: studio?.revenue_share_percentage || 70,
      owner_email: studio?.contact_email || studio?.owner?.email || '',
      status: studio?.status || 'active'
    });
    const [saving, setSaving] = useState(false);
    const [linkStatus, setLinkStatus] = useState(null);

    const handleSave = async () => {
      setSaving(true);
      setLinkStatus(null);
      try {
        let result;
        if (studio) {
          // Update existing studio
          result = await studioService.updateStudio(studio.id, formData);
        } else {
          // Create new studio and auto-link user by email
          if (!formData.owner_email) {
            alert('Owner email is required');
            setSaving(false);
            return;
          }
          result = await studioService.createStudioWithOwner(
            {
              name: formData.name,
              description: formData.description,
              website: formData.website,
              revenue_share_percentage: formData.revenue_share_percentage,
              status: formData.status
            },
            formData.owner_email
          );
        }

        if (result.error) {
          alert('Error saving studio: ' + (result.error.message || result.error));
        } else {
          // Show link status for new studios
          if (!studio && result.userLinked) {
            setSuccessMessage(`Studio created! User ${result.user?.email} has been made a Studio Admin.`);
          } else if (!studio && result.pendingEmail) {
            setSuccessMessage(`Studio created! When ${result.pendingEmail} signs up, they will be auto-linked.`);
          } else {
            setSuccessMessage('Studio saved successfully!');
          }
          onSave();
          loadStudios();
        }
      } catch (error) {
        alert('Error saving studio: ' + error.message);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {studio ? 'Edit Studio' : 'Add Studio'}
            </h3>
            <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Studio Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter studio name"
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter studio description"
                multiline
                rows={3}
              />
            </div>

            <Input
              label="Website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://studio-website.com"
            />

            <Input
              label="Revenue Share %"
              type="number"
              value={formData.revenue_share_percentage}
              onChange={(e) => setFormData({ ...formData, revenue_share_percentage: parseFloat(e.target.value) })}
              placeholder="70"
              min="0"
              max="100"
            />

            <Input
              label="Owner Email"
              type="email"
              value={formData.owner_email}
              onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
              placeholder="owner@studio.com"
              disabled={!!studio} // Can't change owner email for existing studios
            />

            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              options={[
                { value: 'pending', label: 'Pending Review' },
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
                { value: 'rejected', label: 'Rejected' }
              ]}
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Sort studios based on current sort settings
  const sortedStudios = [...filteredStudios].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'games_count':
        aValue = a.games_count || 0;
        bValue = b.games_count || 0;
        break;
      case 'total_play_count':
        aValue = a.total_play_count || 0;
        bValue = b.total_play_count || 0;
        break;
      case 'revenue_share_percentage':
        aValue = a.revenue_share_percentage || 0;
        bValue = b.revenue_share_percentage || 0;
        break;
      case 'created_at':
      default:
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const studioStats = {
    total: studios.length,
    active: studios.filter(s => s.status === 'active').length,
    pending: studios.filter(s => s.status === 'pending').length,
    totalRevenue: 'TBD' // Will be calculated when revenue system is implemented
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border lg:hidden">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold text-foreground">Studio Management</h1>
          </div>
        </div>

        <main className="p-4 lg:p-8 pt-20 lg:pt-8 pb-20 lg:pb-8">
          {/* Success Message Display */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={20} className="text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-green-800">{successMessage}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto text-green-600 hover:text-green-800"
                />
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setError(null)}
                  className="ml-auto text-red-600 hover:text-red-800"
                />
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                Studio Management
              </h1>
              <p className="text-muted-foreground">
                Manage gaming studios, approvals, and revenue sharing
              </p>
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={() => console.log('Export studios')}
              >
                Export
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                onClick={() => setShowStudioModal(true)}
              >
                Add Studio
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-card border border-border rounded-lg mb-6">
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('applications')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'applications'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={18} />
                  <span>Applications</span>
                  {applicationStats.pending > 0 && (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {applicationStats.pending}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('studios')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'studios'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Building" size={18} />
                  <span>Active Studios</span>
                  <span className="text-xs text-muted-foreground">({studioStats.total})</span>
                </div>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'applications' ? 'Total Applications' : 'Total Studios'}
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {activeTab === 'applications' ? applicationStats.total : studioStats.total}
                  </p>
                </div>
                <Icon name={activeTab === 'applications' ? 'FileText' : 'Building'} size={24} className="text-primary" />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'applications' ? 'Pending Review' : 'Active Studios'}
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {activeTab === 'applications' ? applicationStats.pending : studioStats.active}
                  </p>
                </div>
                <Icon name={activeTab === 'applications' ? 'Clock' : 'CheckCircle'} size={24} className={activeTab === 'applications' ? 'text-yellow-500' : 'text-accent'} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'applications' ? 'Approved' : 'Pending Review'}
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {activeTab === 'applications' ? applicationStats.approved : studioStats.pending}
                  </p>
                </div>
                <Icon name={activeTab === 'applications' ? 'CheckCircle' : 'Clock'} size={24} className={activeTab === 'applications' ? 'text-green-500' : 'text-secondary'} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === 'applications' ? 'Rejected' : 'Total Revenue'}
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {activeTab === 'applications' ? applicationStats.rejected : studioStats.totalRevenue}
                  </p>
                </div>
                <Icon name={activeTab === 'applications' ? 'XCircle' : 'DollarSign'} size={24} className={activeTab === 'applications' ? 'text-red-500' : 'text-accent'} />
              </div>
            </div>
          </div>

          {/* Applications Tab Content */}
          {activeTab === 'applications' ? (
            <div className="space-y-6">
              {console.log('🔍 Rendering applications tab - applications:', applications, 'loading:', loading)}
              {loading ? (
                <div className="grid grid-cols-1 gap-6">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
                      <div className="h-3 bg-muted rounded w-full mb-2"></div>
                      <div className="h-3 bg-muted rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : applications.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Applications</h3>
                  <p className="text-muted-foreground">There are currently no studio applications to review.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {applications.map((app) => (
                    <div key={app.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-semibold text-foreground">{app.studio_name}</h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                app.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                              }`}>
                              {app.status}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p><strong>Applicant:</strong> {app.user?.full_name || app.user?.email || 'Unknown'}</p>
                            <p><strong>Email:</strong> {app.user?.email}</p>
                            {app.studio_website && (
                              <p>
                                <strong>Website:</strong>{' '}
                                <a href={app.studio_website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                  {app.studio_website}
                                </a>
                              </p>
                            )}
                            <p><strong>Team Size:</strong> {app.team_size?.replace('-', ' to ')}</p>
                            <p><strong>Submitted:</strong> {new Date(app.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-foreground mb-2">Description:</h4>
                        <p className="text-sm text-muted-foreground">{app.studio_description}</p>
                      </div>

                      {app.rejection_reason && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <h4 className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</h4>
                          <p className="text-sm text-red-700">{app.rejection_reason}</p>
                        </div>
                      )}

                      {app.status === 'pending' && (
                        <div className="flex items-center space-x-3 pt-4 border-t border-border">
                          <Button
                            variant="default"
                            size="sm"
                            iconName="CheckCircle"
                            onClick={() => handleApproveApplication(app.id)}
                            disabled={actionLoading}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            iconName="XCircle"
                            onClick={() => {
                              setSelectedApplication(app);
                              setShowRejectModal(true);
                            }}
                            disabled={actionLoading}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Filters and Search */}
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search studios by name or owner..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      iconName="Search"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      options={[
                        { value: 'all', label: 'All Status' },
                        { value: 'active', label: 'Active' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'suspended', label: 'Suspended' },
                        { value: 'rejected', label: 'Rejected' }
                      ]}
                    />
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      options={[
                        { value: 'created_at', label: 'Date Created' },
                        { value: 'name', label: 'Studio Name' },
                        { value: 'games_count', label: '# Games' },
                        { value: 'total_play_count', label: 'Total Plays' },
                        { value: 'revenue_share_percentage', label: 'Revenue Share %' }
                      ]}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      iconName={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'}
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    />
                  </div>
                </div>

                {selectedStudios.length > 0 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <span className="text-sm text-muted-foreground">
                      {selectedStudios.length} studio(s) selected
                    </span>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('approve')}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('suspend')}
                      >
                        Suspend
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleBulkAction('reject')}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Studios Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                      <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
                      <div className="h-3 bg-muted rounded w-full mb-2"></div>
                      <div className="h-3 bg-muted rounded w-2/3 mb-4"></div>
                      <div className="flex justify-between items-center">
                        <div className="h-6 bg-muted rounded w-16"></div>
                        <div className="h-8 bg-muted rounded w-20"></div>
                      </div>
                    </div>
                  ))
                ) : sortedStudios.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <Icon name="Building" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No studios found</p>
                  </div>
                ) : (
                  sortedStudios.map((studio) => (
                    <div key={studio.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedStudios.includes(studio.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStudios([...selectedStudios, studio.id]);
                              } else {
                                setSelectedStudios(selectedStudios.filter(id => id !== studio.id));
                              }
                            }}
                            className="rounded border-border"
                          />
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon name="Building" size={20} className="text-primary" />
                          </div>
                        </div>

                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${studio.status === 'active' ? 'bg-green-100 text-green-800' :
                            studio.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              studio.status === 'suspended' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                          }`}>
                          {studio.status}
                        </span>
                      </div>

                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-foreground mb-2">{studio.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {studio.description}
                        </p>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Owner:</span>
                            <span className="text-foreground font-medium">{studio.owner_name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Games:</span>
                            <span className="text-foreground font-medium">{studio.games_count}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Revenue Share:</span>
                            <span className="text-foreground font-medium">{studio.revenue_share_percentage}%</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Monthly Revenue:</span>
                            <span className="text-accent font-medium">{studio.monthly_revenue}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <div className="flex space-x-2">
                          {studio.website && (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="ExternalLink"
                              onClick={() => window.open(studio.website, '_blank')}
                            />
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Mail"
                            onClick={() => window.open(`mailto:${studio.owner_email}`, '_blank')}
                          />
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Settings"
                            onClick={() => navigate(`/admin-studio-details?studioId=${studio.id}`)}
                            title="View technical details"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Edit"
                            onClick={() => {
                              setEditingStudio(studio);
                              setShowStudioModal(true);
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleStudioAction('delete', studio.id)}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {showStudioModal && (
        <StudioModal
          studio={editingStudio}
          onClose={() => {
            setShowStudioModal(false);
            setEditingStudio(null);
          }}
          onSave={() => {
            setShowStudioModal(false);
            setEditingStudio(null);
            // Implement save logic
          }}
        />
      )}

      {/* Rejection Modal */}
      {showRejectModal && selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Reject Application
              </h3>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedApplication(null);
                  setRejectionReason('');
                }}
              />
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              You are about to reject the application from <strong>{selectedApplication.studio_name}</strong>.
              Please provide a reason for the rejection.
            </p>

            <div className="mb-4">
              <label className="text-sm font-medium text-foreground block mb-2">
                Rejection Reason <span className="text-destructive">*</span>
              </label>
              <textarea
                placeholder="Explain why this application is being rejected..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={actionLoading}
                rows={4}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedApplication(null);
                  setRejectionReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleRejectApplication(selectedApplication.id, rejectionReason)}
                disabled={actionLoading || !rejectionReason.trim()}
              >
                {actionLoading ? 'Rejecting...' : 'Reject Application'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudioManagement;
